import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import type { Auth, Connection } from 'home-assistant-js-websocket';
import { HAConnectionManager } from '../ha/api';
import type { HAConnectionState } from '../ha/api';
import { clearStoredAuth, logoutHA, resolveHAAuth, stripAuthCallbackParams } from '../ha/auth';
import { loadSettings } from '../settings/loader';
import { useLANReachability } from './useLANReachability';

export type ActiveMode = 'lan' | 'wan' | 'fallback';

export interface UseAdaptiveHAConnectionResult {
  connection: Connection | null;
  state: HAConnectionState;
  activeMode?: ActiveMode;
  disabledReason?: string;
  /** Revokes + clears the current session, then reloads into a fresh login. */
  logout?: () => void;
}

type Target = { url: string; mode: 'lan' | 'fallback' };

export function useAdaptiveHAConnection(): UseAdaptiveHAConnectionResult {
  const settings = useMemo(() => loadSettings(), []);
  const hasFallback = Boolean(settings.ha.fallbackUrl);

  const reachability = useLANReachability(settings.ha.url);

  const target = useMemo<Target | null>(() => {
    if (settings.mockRegistry) return null;

    if (!hasFallback) {
      return { url: settings.ha.url, mode: 'lan' };
    }

    if (
      reachability.mode === 'lan' ||
      reachability.mode === 'unknown' ||
      reachability.mode === 'checking'
    ) {
      return { url: settings.ha.url, mode: 'lan' };
    }
    return { url: settings.ha.fallbackUrl as string, mode: 'fallback' };
  }, [settings, hasFallback, reachability.mode]);

  const stopConnection = useRef<{ auth: Auth; stop: () => void } | null>(null);
  const logoutPending = useRef(false);
  const [connection, setConnection] = useState<Connection | null>(null);
  const [auth, setAuth] = useState<Auth | null>(null);
  const [state, setState] = useState<HAConnectionState>('idle');
  const [disabledReason, setDisabledReason] = useState<string | undefined>(() =>
    settings.mockRegistry ? 'mockRegistry' : undefined,
  );

  useEffect(() => {
    if (settings.mockRegistry) {
      console.log('[ha] mockRegistry enabled — skipping real HA connection');
      setDisabledReason('mockRegistry');
      return;
    }
    setDisabledReason(undefined);
  }, [settings.mockRegistry]);

  // Primitive Effect-Deps statt Objekt-Identitaet: das Target-Objekt wird bei
  // jeder Reachability-Mode-Transition (unknown→checking→lan) neu erzeugt,
  // obwohl url/mode dabei identisch bleiben. Mit dem Objekt als Dep lief der
  // Connect-Effect bei jeder Transition erneut (manager.close() + Reconnect,
  // sichtbar als doppelter WS-Connect beim Startup, RSC-237 Follow-up).
  const targetUrl = target?.url ?? null;
  const targetMode = target?.mode ?? null;

  useEffect(() => {
    if (!targetUrl) return;

    let cancelled = false;
    const controller = new AbortController();
    let manager: HAConnectionManager | null = null;
    logoutPending.current = false;
    const stop = () => {
      cancelled = true;
      controller.abort();
      manager?.close();
      setConnection(null);
      setAuth(null);
    };
    setConnection(null);
    setState('idle');

    // resolveHAAuth() either reuses a stored session/the code+state HA just
    // redirected back with, or — for a first-time/expired visitor — it
    // navigates the browser to /auth/authorize and never resolves (RSC-955:
    // no static token is ever available to fall back to, this is the only
    // path to a connection).
    void (async () => {
      let auth: Auth;
      try {
        auth = await resolveHAAuth(targetUrl, { signal: controller.signal });
      } catch (err) {
        if (cancelled) return;
        console.error('[ha] auth resolution failed', err);
        setState('failed');
        return;
      }
      if (cancelled) return;
      stripAuthCallbackParams();
      setAuth(auth);
      stopConnection.current = { auth, stop };

      manager = new HAConnectionManager({
        auth,
        onStateChange: (s) => {
          if (cancelled) return;
          setState(s);
          if (s === 'auth_invalid') {
            // Session is dead (revoked refresh token) — drop it so a reload
            // forces a fresh login instead of retrying forever (fail-closed).
            clearStoredAuth(targetUrl);
          }
        },
      });

      try {
        const conn = await manager.connect();
        if (cancelled) {
          conn.close();
          return;
        }
        setConnection(conn);
      } catch (err) {
        if (!cancelled) console.error('[ha] connect failed', err);
      }
    })();

    return () => {
      stop();
      if (stopConnection.current?.stop === stop) stopConnection.current = null;
    };
  }, [targetUrl, targetMode]);

  // activeMode reports the LAN/WAN indicator regardless of whether we are
  // actually connected — the reachability probe runs as long as a fallback URL
  // is configured, so the badge stays informative even in mockRegistry mode.
  const activeMode = useMemo<ActiveMode | undefined>(() => {
    if (!hasFallback) return undefined;
    if (reachability.mode === 'wan') {
      return target?.mode === 'fallback' ? 'fallback' : 'wan';
    }
    if (reachability.mode === 'lan') return 'lan';
    return undefined;
  }, [hasFallback, reachability.mode, target]);

  const logout = useCallback(() => {
    const lifecycle = stopConnection.current;
    if (!auth || lifecycle?.auth !== auth || logoutPending.current) return;
    logoutPending.current = true;
    void logoutHA(auth, lifecycle.stop).then(() => {
      if (stopConnection.current === lifecycle) window.location.reload();
    }, () => {
      if (stopConnection.current !== lifecycle) return;
      logoutPending.current = false;
      setState('failed');
      setDisabledReason('Abmeldung nicht abgeschlossen. Bitte erneut abmelden.');
      setAuth(auth); // expose cleanup retry only; connection remains closed
    });
  }, [auth]);

  return { connection, state, activeMode, disabledReason, logout: auth ? logout : undefined };
}
